import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { CustomMultiSelect } from "./CustomSelect.tsx";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "sonner";
import styles from "./ExportDataModal.module.css";

interface PumpChannel {
  value: string;
  label: string;
}

interface ExportDataModalProps {
  show: boolean;
  onHide: () => void;
  theme: string;
  selectedChannels: PumpChannel[];
  availableChannels: PumpChannel[];
  fromDate: Date | null;
  toDate: Date | null;
  activeTab: "fleet" | "asset";
  fleetNumber?: number;
  pumpNames?: string[];
  backendUrl: string;
}

const ExportDataModal: React.FC<ExportDataModalProps> = ({
  show,
  onHide,
  theme,
  selectedChannels,
  availableChannels,
  fromDate: initialFromDate,
  toDate: initialToDate,
  activeTab,
  fleetNumber,
  pumpNames,
  backendUrl,
}) => {
  const [exportChannels, setExportChannels] =
    useState<PumpChannel[]>(selectedChannels);
  const [exportFromDate, setExportFromDate] = useState<Date | null>(
    initialFromDate,
  );
  const [exportToDate, setExportToDate] = useState<Date | null>(initialToDate);
  const [resolution, setResolution] = useState<"1sec" | "1min" | "1hr">("1min");
  const [usePrev24Hours, setUsePrev24Hours] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showLargeExportWarning, setShowLargeExportWarning] = useState(false);

  useEffect(() => {
    if (show) {
      setExportChannels(selectedChannels);
      setExportFromDate(initialFromDate);
      setExportToDate(initialToDate);
      setUsePrev24Hours(false);
      setShowLargeExportWarning(false);
    }
  }, [show, selectedChannels, initialFromDate, initialToDate]);

  useEffect(() => {
    if (usePrev24Hours) {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      setExportFromDate(yesterday);
      setExportToDate(now);
    }
  }, [usePrev24Hours]);

  const getMaxDateRange = (
    res: "1sec" | "1min" | "1hr",
  ): { ms: number; label: string } => {
    switch (res) {
      case "1sec":
        return { ms: (24 * 60 * 60 + 1) * 1000, label: "1 day" };
      case "1min":
        return { ms: 31 * 24 * 60 * 60 * 1000, label: "1 month" };
      case "1hr":
        return { ms: 5 * 365 * 24 * 60 * 60 * 1000, label: "5 years" };
    }
  };

  const shouldWarnLargeExport = () => {
    if (!exportFromDate || !exportToDate) return false;

    const dateRangeMs = exportToDate.getTime() - exportFromDate.getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;

    return resolution === "1sec" && dateRangeMs > oneDayMs;
  };

  const runExport = async () => {
    setIsExporting(true);

    try {
      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");
      const params = new URLSearchParams();
      params.append("channels", exportChannels.map((ch) => ch.value).join(","));
      params.append("resolution", resolution);
      params.append(
        "start_time",
        Math.floor(exportFromDate!.getTime()).toString(),
      );
      params.append("end_time", Math.floor(exportToDate!.getTime()).toString());

      const routeParams = new URLSearchParams(window.location.search);
      const routeFleetParam = routeParams.get("fleet");
      const routePumpParam = routeParams.get("pump");

      const routeFleet =
        routeFleetParam !== null && routeFleetParam.trim() !== ""
          ? Number(routeFleetParam)
          : undefined;

      const resolvedFleetNumber =
        routeFleet !== undefined && Number.isFinite(routeFleet)
          ? routeFleet
          : fleetNumber;

      const resolvedPumpNames =
        routePumpParam && routePumpParam.trim() !== ""
          ? [routePumpParam.trim()]
          : (pumpNames ?? []).filter((name) => name && name.trim() !== "");

      if (resolvedFleetNumber !== undefined) {
        params.append("fleet", resolvedFleetNumber.toString());
      }

      if (resolvedPumpNames.length > 0) {
        resolvedPumpNames.forEach((name) => {
          params.append("pump_name", name);
        });
      }

      console.debug("[ExportDataModal] export filters", {
        activeTab,
        fleetNumberProp: fleetNumber,
        pumpNamesProp: pumpNames,
        routeFleet: routeFleetParam,
        routePump: routePumpParam,
        resolvedFleetNumber,
        resolvedPumpNames,
      });

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 60_000);

      let response: Response;
      try {
        response = await fetch(
          `${backendUrl}/pumps/export?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );
      } finally {
        window.clearTimeout(timeoutId);
      }

      console.debug("[ExportDataModal] export response", {
        status: response.status,
        mode: response.headers.get("X-Export-Mode"),
        rowCount: response.headers.get("X-Export-Row-Count"),
        rowUpperBound: response.headers.get("X-Export-Row-Upper-Bound"),
        rawFallbackChannels: response.headers.get(
          "X-Export-Raw-Fallback-Channels",
        ),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Export failed");
        } else {
          throw new Error(`Export failed with status ${response.status}`);
        }
      }

      const csvBlob = await response.blob();

      if (!csvBlob || csvBlob.size === 0) {
        toast.warning("No data found for the selected criteria");
        return;
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `pump_export_${resolution}_${Math.floor(exportFromDate!.getTime())}_${Math.floor(exportToDate!.getTime())}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "").trim();
        }
      }

      const blob =
        csvBlob.type === "text/csv"
          ? csvBlob
          : new Blob([csvBlob], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Data exported successfully");
      onHide();
    } catch (error: any) {
      console.error("Export error:", error);
      if (error?.name === "AbortError") {
        toast.error(
          "Export timed out after 60 seconds. Verify the pumps export index is installed.",
        );
      } else {
        toast.error(error.message || "Failed to export data");
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    if (exportChannels.length === 0) {
      toast.warning("Please select at least one channel");
      return;
    }

    if (!exportFromDate || !exportToDate) {
      toast.warning("Please select a date range");
      return;
    }

    if (exportFromDate >= exportToDate) {
      toast.warning("From date must be before To date");
      return;
    }

    if (shouldWarnLargeExport()) {
      setShowLargeExportWarning(true);
      return;
    }

    await runExport();
  };

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#303335" : "#ffffff",
      borderColor: theme === "dark" ? "#3d4247" : "#d0d5dd",
      minHeight: "38px",
      boxShadow: state.isFocused
        ? theme === "dark"
          ? "0 0 0 1px #4a9eff"
          : "0 0 0 1px #0066cc"
        : "none",
      "&:hover": {
        borderColor: theme === "dark" ? "#4a5568" : "#b0b5bd",
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#303335" : "#ffffff",
      border: theme === "dark" ? "1px solid #3d4247" : "1px solid #e6e6e6",
      boxShadow:
        theme === "dark"
          ? "0 4px 12px rgba(0, 0, 0, 0.4)"
          : "0 4px 12px rgba(0, 0, 0, 0.1)",
      zIndex: 9999,
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "200px",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? theme === "dark"
          ? "#404448"
          : "#f0f0f0"
        : state.isSelected
          ? theme === "dark"
            ? "#4a5568"
            : "#e6e9ec"
          : theme === "dark"
            ? "#303335"
            : "#ffffff",
      color: theme === "dark" ? "#e5e5e5" : "#333333",
      cursor: "pointer",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#4a5568" : "#e6e9ec",
      borderRadius: "4px",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#ffffff" : "#333333",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "2px 6px",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#ffffff" : "#666666",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#dc3545",
        color: "#ffffff",
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "4px 8px",
      maxHeight: "120px",
      overflowY: "auto",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#8b9299" : "#999999",
    }),
  };

  if (!show) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={onHide} />

      <div
        className={`${styles.modal} ${theme === "dark" ? styles.dark : styles.light}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 className={styles.title}>Export Data</h3>
          <button
            className={styles.closeButton}
            onClick={onHide}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.channelSection}>
            <CustomMultiSelect
              options={availableChannels}
              selected={exportChannels}
              onChange={(sel) => setExportChannels(sel as PumpChannel[])}
              placeholder="Search channels..."
            />
          </div>

          <div className={styles.topRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="radio"
                checked={usePrev24Hours}
                onChange={() => setUsePrev24Hours(true)}
                className={styles.radioInput}
              />
              <span>Last 24 Hours</span>
            </label>
            <div className={styles.frequencyContainer}>
              <span className={styles.frequencyLabel}>Data Frequency</span>

              <div className={styles.frequencyOptions}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="resolution"
                    value="1sec"
                    checked={resolution === "1sec"}
                    onChange={() => setResolution("1sec")}
                  />
                  1 Sec
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="resolution"
                    value="1min"
                    checked={resolution === "1min"}
                    onChange={() => setResolution("1min")}
                  />
                  1 Min
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="resolution"
                    value="1hr"
                    checked={resolution === "1hr"}
                    onChange={() => setResolution("1hr")}
                  />
                  1 Hr
                </label>
              </div>
            </div>
          </div>

          <div className={styles.dateRow}>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>From</label>
              <DatePicker
                selected={exportFromDate}
                onChange={(date: Date | null) => {
                  setExportFromDate(date);
                  setUsePrev24Hours(false);
                }}
                showTimeSelect
                dateFormat="MM/dd/yyyy, h:mm aa"
                className={styles.dateInput}
                placeholderText="Select start date"
                popperPlacement="bottom-start"
              />
            </div>

            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>To</label>
              <DatePicker
                selected={exportToDate}
                onChange={(date: Date | null) => {
                  setExportToDate(date);
                  setUsePrev24Hours(false);
                }}
                showTimeSelect
                dateFormat="MM/dd/yyyy, h:mm aa"
                className={styles.dateInput}
                placeholderText="Select end date"
                popperPlacement="bottom-start"
              />
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.generateButton}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Generate CSV"}
          </button>
        </div>
      </div>

      {showLargeExportWarning && (
        <div
          className={styles.warningOverlay}
          onClick={() => setShowLargeExportWarning(false)}
        >
          <div
            className={styles.warningModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.warningTitleRow}>
              <span className={styles.warningIcon}>⚠️</span>
              <h4 className={styles.warningTitle}>Large Data Download</h4>
            </div>

            <p className={styles.warningMessage}>
              This request will generate a very large dataset.
            </p>

            <p className={styles.warningSubtext}>
              It may take several minutes and could fail due to timeout.
              Consider using 1 Min or a shorter date range.
            </p>

            <div className={styles.warningActions}>
              <button
                type="button"
                className={styles.warningCancelButton}
                onClick={() => setShowLargeExportWarning(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className={styles.warningProceedButton}
                onClick={async () => {
                  setShowLargeExportWarning(false);
                  await runExport();
                }}
              >
                Download Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportDataModal;
